/**
 * Reveals the Shopify Inbox chat widget only after the visitor has scrolled past
 * the first screen, and tucks it away again when they scroll back to the top.
 *
 * The widget itself is injected by the Inbox app some time after load, so this
 * toggles a class on <html> rather than touching the widget directly — the CSS
 * then applies whenever the app gets around to inserting `#shopify-chat`.
 */

/** Fraction of the viewport that must scroll past before the chat appears. */
const REVEAL_RATIO = 0.6;

/** Above this height the widget is showing a conversation, not just the button. */
const PANEL_OPEN_HEIGHT = 200;

/** How long to wait for the Inbox app before giving up on watching it. */
const APP_LOAD_TIMEOUT = 20000;

const root = document.documentElement;

let ticking = false;
let chatIsOpen = false;

function update() {
  ticking = false;

  // Never pull the widget off screen mid-conversation.
  const shouldReveal = chatIsOpen || window.scrollY > window.innerHeight * REVEAL_RATIO;

  root.classList.toggle('chat-revealed', shouldReveal);
}

function requestUpdate() {
  if (ticking) return;

  ticking = true;
  requestAnimationFrame(update);
}

/**
 * Tracks whether the chat panel is open, inferred from the widget's height —
 * its contents live in a cross-origin iframe, so its state is not readable.
 * @param {HTMLElement} chat
 */
function watchOpenState(chat) {
  if (!('ResizeObserver' in window)) return;

  const observer = new ResizeObserver(() => {
    const isOpen = chat.offsetHeight > PANEL_OPEN_HEIGHT;

    if (isOpen === chatIsOpen) return;

    chatIsOpen = isOpen;
    update();
  });

  observer.observe(chat);
}

function findChat() {
  const chat = document.getElementById('shopify-chat');

  if (chat) {
    watchOpenState(chat);
    return;
  }

  const observer = new MutationObserver(() => {
    const injected = document.getElementById('shopify-chat');

    if (!injected) return;

    observer.disconnect();
    watchOpenState(injected);
  });

  observer.observe(document.body, { childList: true });
  setTimeout(() => observer.disconnect(), APP_LOAD_TIMEOUT);
}

window.addEventListener('scroll', requestUpdate, { passive: true });
window.addEventListener('resize', requestUpdate, { passive: true });

update();
findChat();
